import { AuthConsumerRenderProps, withAuthContext } from 'auth/AuthContext';
import axios, { CancelTokenSource } from 'axios';
import DatePicker from 'components/final-form/DatePicker';
import { DropdownOption, mapToDropdownOptionArray } from 'components/final-form/dropdownUtils';
import Input from 'components/final-form/Input';
import RadioGroup from 'components/final-form/RadioGroup';
import Select from 'components/final-form/Select';
import FormButtonsRow from 'components/layout/FormButtonsRow';
import FormContainer from 'components/layout/FormContainer';
import FormContent from 'components/layout/FormContent';
import PraxFormScrollableContainer from 'components/layout/PraxFormScrollableContainer';
import PxGrid from 'components/layout/PxGrid';
import LoaderComponent from 'components/LoaderComponent';
import { FormApi } from 'final-form';
import moment from 'moment';
import React from 'react';
import { Field, Form as FinalForm, FormRenderProps } from 'react-final-form';
import { MessageSourceProps, withMessages } from 'react-message-source';
import { Button, Grid, Message, RadioProps } from 'semantic-ui-react';
import { getPatientGuarantorsAfterCardDataRead } from 'services/patientGuarantorServices';
import styled from 'styled-components';
import { CountryDto, PatientCardDataDto, PatientGuarantorDto } from 'ts-types/api.types';
import { ChromeExtensionStatus, getChromeExtensionStatus, readHealthCard } from 'utils/chomeExtensionUtils';
import { errorUtils } from 'utils/errorUtils';
import { ahvValidator } from 'utils/validatorUtils';

const StyledMessage = styled(Message)`
  margin-bottom: 0 !important;
  width: 100%;
`;

const InfoDiv = styled.div`
  & .info-message {
    margin-top: 2rem;
    min-height: 105px;
  }

  & .info-message .download-link {
    margin-top: 1.5rem;
  }

  & .actions .button:first-child {
    margin-left: 0;
  }
`;

const StyledWarningRow = styled(Grid.Row)`
  margin-bottom: .5rem;
`;

const PatientDataDiv = styled.div`

  margin-top: .5rem;
  color: #757575;

  &.no-match {
    color: #794b02;
  }
`;

export interface CardData {
  name: string,
  firstName: string,
  birthDate: string,
  ahvNumber: string,
  sex: number,
  country: string,
  guarantorName: string,
  institutionId: string,
  cardId: string,
  expiryDate: string
}

export enum CardReaderState {
  INITIAL,
  NO_EXTENSION,
  NO_HOST_APPLICATION,
  OUTDATED_EXTENSION,
  OUTDATED_HOST_APP,
  NO_CARD,
  CARD_READ,
  ERROR
}

interface Props extends AuthConsumerRenderProps, MessageSourceProps {
  patientForm: FormApi,
  patientId: number,
  patientGuarantors: Array<PatientGuarantorDto>,
  countries: Array<CountryDto>,
  genders: Array<RadioProps>,
  closeModal: () => void,
  updatePatientDataFromCard: (patientGuarantors: Array<PatientGuarantorDto>,
                              cardData: Partial<CardData>, form: FormApi) => void
}

interface State {
  cardReaderState: CardReaderState,
  cardData: Partial<CardData>,
  countries: Array<DropdownOption>,
  cardDataUpdated: boolean,
  renderLoader: boolean,
  errorMessage?: string,
  cancelTokenSource: CancelTokenSource
}

class PatientCardData extends React.Component<Props, State> {

  constructor(props: Props) {
    super(props);

    const {countries = []} = props;
    const cancelTokenSource = axios.CancelToken.source();

    this.state = {
      cardReaderState: CardReaderState.INITIAL,
      cardDataUpdated: false,
      countries: mapToDropdownOptionArray(countries, "name", "shortName", "shortName"),
      cardData: {},
      renderLoader: false,
      cancelTokenSource
    };
  }

  componentDidMount() {
    this.readCard();
  }

  readCard = async () => {
    const {config} = this.props;

    const extStatus = await getChromeExtensionStatus(config);

    if (ChromeExtensionStatus.NO_EXTENSION === extStatus) {
      this.setCardReaderState(CardReaderState.NO_EXTENSION);
    } else if (ChromeExtensionStatus.OUTDATED_EXTENSION === extStatus) {
      this.setCardReaderState(CardReaderState.OUTDATED_EXTENSION);
    } else if (ChromeExtensionStatus.NO_HOST_APPLICATION === extStatus) {
      this.setCardReaderState(CardReaderState.NO_HOST_APPLICATION);
    } else if (ChromeExtensionStatus.OUTDATED_HOST_APP === extStatus) {
      this.setCardReaderState(CardReaderState.OUTDATED_HOST_APP);
    } else if (ChromeExtensionStatus.OK === extStatus) {

      const cardReaderResponse: any = await readHealthCard();

      const payload = cardReaderResponse.payload;
      if (cardReaderResponse.status === 'ERROR' && payload.errorCode === "NO_PATIENT_DATA") {
        this.setCardReaderState(CardReaderState.NO_CARD);
      } else if (cardReaderResponse.status === 'ERROR') {
        this.setCardReaderState(CardReaderState.ERROR);
      } else if (cardReaderResponse.status === 'RESULT') {
        const responseCardData = payload;
        const cardData: CardData = {
          name: responseCardData.lastName,
          firstName: responseCardData.firstName,
          birthDate: responseCardData.dateOfBirth,
          ahvNumber: responseCardData.insuranceNumber,
          sex: responseCardData.gender === "1" ? 1 : 0,
          country: responseCardData.issuingState,
          guarantorName: responseCardData.nameOfTheInstitution,
          institutionId: responseCardData.identificationNumberOfTheInstitution,
          cardId: responseCardData.insuredPersonNumber,
          expiryDate: responseCardData.expiryDate
        }
        this.setState({
          cardReaderState: CardReaderState.CARD_READ,
          cardData: cardData,
          cardDataUpdated: true
        });
      }

    }
  }

  setCardReaderState(state: CardReaderState) {
    this.setState({
      cardReaderState: state
    });
  }

  render(): React.ReactNode {
    const {cardReaderState, cardData} = this.state;
    const {closeModal, getMessage} = this.props;

    if (cardReaderState === CardReaderState.INITIAL) {
      return this.renderLoader();
    } else if (cardReaderState === CardReaderState.NO_EXTENSION) {
      return (
          <InfoDiv>
            <div className="info-message">
              Please install the Praxmedic Native Tools chrome extension, from the link below, to be able to read the
              health card.
              <div>
                <a href="https://chrome.google.com/webstore/detail/iggeppehjnmjeeicldkpjopdofglljei?authuser=0&hl=en"
                   target="_blank">Praxmedic Native Tools</a>
              </div>
            </div>
            <div className="actions">
              <Button type="button" compact onClick={closeModal}>
                {getMessage("button.cancel")}
              </Button>
            </div>
          </InfoDiv>
      )
    } else if (cardReaderState === CardReaderState.OUTDATED_EXTENSION) {
      return (
          <InfoDiv>
            <div className="info-message">
              You have an older version of the Praxmedic Native Tools chrome extension.<br/>
              Please update the extension using the link below.
              <div className="download-link">
                <a href="https://chrome.google.com/webstore/detail/iggeppehjnmjeeicldkpjopdofglljei?authuser=0&hl=en"
                   target="_blank">Praxmedic Native Tools</a>
              </div>
            </div>
            <div className="actions">
              <Button type="button" compact onClick={closeModal}>
                {getMessage("button.cancel")}
              </Button>
            </div>
          </InfoDiv>
      )
    } else if (cardReaderState === CardReaderState.NO_HOST_APPLICATION) {
      return (
          <InfoDiv>
            <div className="info-message">
              Please install the Praxmedic Native Tools host application.
              <div>
                <a href="https://praxmedic.ch/native-tools" target="_blank">Download host application installer</a>
              </div>
            </div>
            <div className="actions">
              <Button type="button" compact onClick={closeModal}>
                {getMessage("button.cancel")}
              </Button>
            </div>
          </InfoDiv>
      )
    } else if (cardReaderState === CardReaderState.OUTDATED_HOST_APP) {
      return (
          <InfoDiv>
            <div className="info-message">
              You have an older version of the Praxmedic Native Tools host application.<br/>
              Please update the host application using the link below.
              <div className="download-link">
                <a href="https://praxmedic.ch/native-tools" target="_blank">Download host application installer</a>
              </div>
            </div>
            <div className="actions">
              <Button type="button" compact onClick={closeModal}>
                {getMessage("button.cancel")}
              </Button>
            </div>
          </InfoDiv>
      )
    } else if (cardReaderState === CardReaderState.NO_CARD) {
      return (
          <InfoDiv>
            <div className="info-message">
              No health card found.
            </div>
            <div className="actions">
              <Button type="button" compact onClick={closeModal}>
                {getMessage("button.cancel")}
              </Button>
            </div>
          </InfoDiv>
      )
    } else if (cardReaderState === CardReaderState.ERROR) {
      return (
          <InfoDiv>
            <div className="info-message">
              Something went wrong while trying to read the health card.
            </div>
            <div className="actions">
              <Button type="button" compact onClick={closeModal}>
                {getMessage("button.cancel")}
              </Button>
            </div>
          </InfoDiv>
      )
    }

    return (
        <FinalForm
            onSubmit={this.handleSubmit}
            subscription={{pristine: true}}
            render={this.renderForm}
            initialValues={cardData}
        />
    );
  }

  renderLoader = () => {
    const {getMessage} = this.props;
    return <LoaderComponent message={getMessage("patient.card.loading")}/>;
  };

  handleSubmit = (values: Partial<CardData>) => {
    const {
      patientForm,
      closeModal,
      patientGuarantors,
      patientId,
      updatePatientDataFromCard
    } = this.props;
    const {cancelTokenSource} = this.state;

    this.setState({
      renderLoader: true
    });

    const patientCardDataDto: PatientCardDataDto = {
      patientId: patientId,
      bagNumber: values.institutionId!,
      healthCardNumber: values.cardId!,
      expiryDate: values.expiryDate!,
      patientGuarantorDtos: patientGuarantors
    };

    getPatientGuarantorsAfterCardDataRead(cancelTokenSource, patientCardDataDto)
        .then(data => {
          updatePatientDataFromCard(data, values, patientForm);

          closeModal();
        })
        .catch(this.handleError)
        .finally(() => {
          this.setState({
            renderLoader: false
          });
        });
  };

  handleError = (error: any) => {
    const {getMessage} = this.props;

    const knownErrors = [errorUtils.unknownBAGNumber];
    let errorMessage = "";

    let specificError = undefined;
    if (error.response.data && error.response.data.violations) {
      specificError = error.response.data.violations
          .find((violation: any) => knownErrors.includes(violation.errorCode));
    }
    if (specificError) {
      errorMessage = getMessage(`error.${specificError.errorCode}`)
    } else {
      errorMessage = getMessage('error.general');
    }

    this.setState({errorMessage})
  };

  renderForm = ({handleSubmit, submitting}: FormRenderProps): React.ReactNode => {
    const {closeModal, getMessage, genders, patientForm} = this.props;
    const {cardData, cardDataUpdated, countries, errorMessage} = this.state;

    const patientValues = patientForm.getState().values;

    const dataNotMatch = patientValues.name !== cardData.name
        || patientValues.firstName !== cardData.firstName
        || patientValues.birthDate !== cardData.birthDate;

    return (
        <form onSubmit={handleSubmit}>
          {errorMessage && (
              <StyledMessage color="red" onDismiss={() => this.setState({errorMessage: undefined})}>
                {errorMessage}
              </StyledMessage>
          )}
          {
              dataNotMatch &&
              <StyledWarningRow>
                <Grid.Column width={16}>
                  <Message warning>
                    {getMessage("patientCentral.checkIn.dataNotMatch")}
                  </Message>
                </Grid.Column>
              </StyledWarningRow>
          }
          <FormContainer>
            <FormContent>
              <PraxFormScrollableContainer height={"500px"}>
                <PxGrid>
                  <Grid.Row>
                    <Grid.Column width={5} verticalAlign="middle">
                      {getMessage("patient.name")}
                    </Grid.Column>
                    <Grid.Column width={11} verticalAlign="middle">
                      <Field
                          fluid
                          name="name"
                          component={Input}
                          disabled
                      />
                      {patientValues && patientValues.name && (
                          <PatientDataDiv className={patientValues.name !== cardData.name ? "no-match" : ""}>
                            {patientValues.name}
                          </PatientDataDiv>
                      )}
                    </Grid.Column>
                  </Grid.Row>
                  <Grid.Row>
                    <Grid.Column width={5} verticalAlign="middle">
                      {getMessage("patient.firstName")}
                    </Grid.Column>
                    <Grid.Column width={11} verticalAlign="middle">
                      <Field
                          fluid
                          name="firstName"
                          component={Input}
                          disabled
                      />
                      {patientValues && patientValues.firstName && (
                          <PatientDataDiv className={patientValues.firstName !== cardData.firstName ? "no-match" : ""}>
                            {patientValues.firstName}
                          </PatientDataDiv>
                      )}
                    </Grid.Column>
                  </Grid.Row>
                  <Grid.Row>
                    <Grid.Column width={5} verticalAlign="middle">
                      {getMessage("patient.birthDate")}
                    </Grid.Column>
                    <Grid.Column width={11} verticalAlign="middle">
                      <Field
                          fluid
                          name="birthDate"
                          component={DatePicker}
                          disabled
                      />
                      {patientValues && patientValues.birthDate && (
                          <PatientDataDiv className={patientValues.birthDate !== cardData.birthDate ? "no-match" : ""}>
                            {moment(patientValues.birthDate).format("DD.MM.YYYY")}
                          </PatientDataDiv>
                      )}
                    </Grid.Column>
                  </Grid.Row>
                  <Grid.Row>
                    <Grid.Column width={5} verticalAlign="middle">
                      {getMessage("patient.ahvNumber")}
                    </Grid.Column>
                    <Grid.Column width={11} verticalAlign="middle">
                      <Field
                          fluid
                          name="ahvNumber"
                          component={Input}
                          validate={ahvValidator}
                          disabled
                      />
                    </Grid.Column>
                  </Grid.Row>
                  <Grid.Row>
                    <Grid.Column width={5} verticalAlign="middle">
                      {getMessage("patient.sex")}
                    </Grid.Column>
                    <Grid.Column width={11} verticalAlign="middle">
                      <Field
                          fluid
                          name="sex"
                          component={RadioGroup}
                          radioDefinitions={genders}
                          disabled
                      />
                    </Grid.Column>
                  </Grid.Row>
                  <Grid.Row>
                    <Grid.Column width={5} verticalAlign="middle">
                      {getMessage("patient.countryId")}
                    </Grid.Column>
                    <Grid.Column width={11} verticalAlign="middle">
                      <Field
                          fluid
                          name="country"
                          component={Select}
                          options={countries}
                          disabled
                      />
                    </Grid.Column>
                  </Grid.Row>
                  <Grid.Row>
                    <Grid.Column width={5} verticalAlign="middle">
                      {getMessage("patient.card.guarantorName")}
                    </Grid.Column>
                    <Grid.Column width={11} verticalAlign="middle">
                      <Field
                          fluid
                          name="guarantorName"
                          component={Input}
                          disabled
                      />
                    </Grid.Column>
                  </Grid.Row>
                  <Grid.Row>
                    <Grid.Column width={5} verticalAlign="middle">
                      {getMessage("patient.card.institutionId")}
                    </Grid.Column>
                    <Grid.Column width={11} verticalAlign="middle">
                      <Field
                          fluid
                          name="institutionId"
                          component={Input}
                          disabled
                      />
                    </Grid.Column>
                  </Grid.Row>
                  <Grid.Row>
                    <Grid.Column width={5} verticalAlign="middle">
                      {getMessage("patient.card.cardId")}
                    </Grid.Column>
                    <Grid.Column width={11} verticalAlign="middle">
                      <Field
                          fluid
                          name="cardId"
                          component={Input}
                          disabled
                      />
                    </Grid.Column>
                  </Grid.Row>
                  <Grid.Row>
                    <Grid.Column width={5} verticalAlign="middle">
                      {getMessage("patient.card.expiryDate")}
                    </Grid.Column>
                    <Grid.Column width={11} verticalAlign="middle">
                      <Field
                          fluid
                          name="expiryDate"
                          component={DatePicker}
                          disabled
                      />
                    </Grid.Column>
                  </Grid.Row>
                </PxGrid>
              </PraxFormScrollableContainer>
            </FormContent>

            <FormButtonsRow>
              <Button type="submit" compact primary
                      disabled={submitting || !cardDataUpdated}>{getMessage("patient.card.update")}</Button>
              <Button type="button" compact disabled={submitting}
                      onClick={closeModal}>{getMessage("button.cancel")}</Button>
            </FormButtonsRow>
          </FormContainer>
        </form>
    );
  }

}

export default withAuthContext(withMessages(PatientCardData));


